import { useEffect, useState } from 'react'
import { useMediaQuery } from '@mui/material'
import Box from '@mui/joy/Box'
import Button from '@mui/joy/Button'
import { useTranslation } from 'react-i18next'
import { Link, useLocation } from 'react-router-dom'

import {
    getCheckoutFallbackPath,
    getCheckoutFlow,
    normalizeCheckoutLocale,
    type CheckoutFlow,
} from '@/checkout-paths'
import { WEB_PROFILE_ROUTE_SEGMENTS } from '@/constants'
import {
    CheckoutDesktopContent,
    CheckoutMobileContent,
} from '@/components/checkout/page/content'
import UnsupportedIosAppCheckout from '@/components/checkout/unsupported-ios-app-checkout'
import { useCheckoutFormController } from '@/components/checkout/page/controller'
import {
    CheckoutBookedState,
    CheckoutExpiredState,
    CheckoutLoadingState,
    CheckoutMessageState,
    CheckoutPaymentResolutionOverlay,
} from '@/components/checkout/page/states'
import {
    buildCheckoutViewModel,
    hasAllocatedCheckoutReservation,
    type CheckoutViewModel,
} from '@/components/checkout/page/view-model'
import { useGetActiveReservationAndCheckoutViewStatus } from '@/hooks/api/use-reservation-checkout'
import { useCheckoutTracking } from '@/hooks/use-checkout-tracking'
import { ROUTE_SEGMENTS } from '@/pwa/constants'
import { isUnsupportedIosAppWrapper } from '@/stripe-checkout'
import { getWebAppConfigValue } from '@/web-app-root'

type CheckoutViewProps = {
    onBack?: () => void
    isPwa?: boolean
    onPay?: () => void
}

function CheckoutAllocatedView({
    viewModel,
    onBack,
    onPay,
    isPwa,
    checkoutFlow,
}: CheckoutViewProps & {
    viewModel: CheckoutViewModel
    checkoutFlow: CheckoutFlow
}) {
    const isDesktop = useMediaQuery('(min-width: 816px)')
    const [benefitCodePaymentExpanded, setBenefitCodePaymentExpanded] = useState(false)
    const { trackBeginPayment } = useCheckoutTracking({
        isPwa: Boolean(isPwa),
        checkoutFlow,
        beginCheckoutTracking: viewModel.beginCheckoutTracking,
    })
    const controller = useCheckoutFormController({
        mode: viewModel.checkoutMode,
        contactSummaryValues: viewModel.reservationContactInfoValues,
        contactFormValues: viewModel.reservationContactInfoValues,
        invoiceRequired: viewModel.invoiceRequired,
        vehicleValues: viewModel.vehicleInfoValues,
        currentUserCars: viewModel.currentUserCars,
        shouldPrefillCheckoutInfo: viewModel.prefillCheckoutInfo,
        phoneCountryBillingHint: viewModel.phoneCountryBillingHint,
        isPwa,
        onPay,
        onBeginPayment: trackBeginPayment,
    })

    useEffect(() => {
        if (viewModel.availableBenefitCodes.length === 0) {
            setBenefitCodePaymentExpanded(false)
        }
    }, [viewModel.availableBenefitCodes.length])

    if (isDesktop) {
        return (
            <CheckoutDesktopContent
                viewModel={viewModel}
                controller={controller}
                benefitCodePaymentExpanded={benefitCodePaymentExpanded}
                onBenefitCodePaymentExpandedChange={setBenefitCodePaymentExpanded}
                onBack={onBack}
            />
        )
    }

    return (
        <CheckoutMobileContent
            viewModel={viewModel}
            controller={controller}
            benefitCodePaymentExpanded={benefitCodePaymentExpanded}
            onBenefitCodePaymentExpandedChange={setBenefitCodePaymentExpanded}
            isPwa={isPwa}
            onBack={onBack}
        />
    )
}

export default function CheckoutView(props: CheckoutViewProps) {
    if (isUnsupportedIosAppWrapper(Boolean(props.isPwa))) {
        return <UnsupportedIosAppCheckout onBack={props.onBack} />
    }

    return <SupportedCheckoutView {...props} />
}

function SupportedCheckoutView({
    onBack,
    isPwa,
    onPay,
}: CheckoutViewProps) {
    const { t, i18n } = useTranslation()
    const location = useLocation()
    const activeReservationQuery = useGetActiveReservationAndCheckoutViewStatus()
    const locale = normalizeCheckoutLocale(i18n.resolvedLanguage || i18n.language)
    const isLoggedIn = Boolean(getWebAppConfigValue('user-id'))
    const garageListPath = isPwa ? `/pwa/${ROUTE_SEGMENTS.search}` : getCheckoutFallbackPath(locale)
    const myReservationsPath = isPwa
        ? `/pwa/${ROUTE_SEGMENTS.myReservations}`
        : isLoggedIn
            ? `/${locale}/${WEB_PROFILE_ROUTE_SEGMENTS.profile[locale]}/${WEB_PROFILE_ROUTE_SEGMENTS.reservations[locale]}`
            : null
    const garageListAction = (
        <Button
            component={Link}
            to={garageListPath}
            variant="solid"
            sx={{
                fontSize: '15px',
                padding: '0.875rem 1.25rem',
                borderRadius: '7px',
                backgroundColor: 'customBlack.main',
                mt: 1,
            }}
        >
            {t('checkout.errors.backToGarageList')}
        </Button>
    )

    if (activeReservationQuery.viewStatus === 'expired') {
        return (
            <CheckoutExpiredState
                title={t('checkout.errors.expiredTitle')}
                text={t('checkout.errors.expiredText')}
                action={garageListAction}
            />
        )
    }

    if (activeReservationQuery.viewStatus === 'loading') {
        return <CheckoutLoadingState />
    }

    if (activeReservationQuery.viewStatus === 'booked') {
        return (
            <CheckoutBookedState
                title={t('checkout.booked.title')}
                text={t('checkout.booked.text')}
                action={(
                    <Box
                        sx={{
                            display: 'flex',
                            flexDirection: 'column',
                            alignItems: 'stretch',
                            gap: 1.5,
                            mt: 1,
                        }}
                    >
                        {myReservationsPath && isPwa ? (
                            <Button
                                component={Link}
                                to={myReservationsPath}
                                variant="solid"
                                sx={{
                                    fontSize: '15px',
                                    padding: '0.875rem 1.25rem',
                                    borderRadius: '7px',
                                    backgroundColor: 'customBlack.main',
                                }}
                            >
                                {t('checkout.booked.myReservations')}
                            </Button>
                        ) : null}
                        {myReservationsPath && !isPwa ? (
                            <Button
                                component="a"
                                href={myReservationsPath}
                                variant="solid"
                                sx={{
                                    fontSize: '15px',
                                    padding: '0.875rem 1.25rem',
                                    borderRadius: '7px',
                                    backgroundColor: 'customBlack.main',
                                }}
                            >
                                {t('checkout.booked.myReservations')}
                            </Button>
                        ) : null}
                        <Button
                            component={Link}
                            to={garageListPath}
                            variant={myReservationsPath ? 'outlined' : 'solid'}
                            sx={{
                                fontSize: '15px',
                                padding: '0.875rem 1.25rem',
                                borderRadius: '7px',
                                ...(myReservationsPath ? {
                                    borderColor: 'customBlack.main',
                                    color: 'customBlack.main',
                                } : {
                                    backgroundColor: 'customBlack.main',
                                }),
                            }}
                        >
                            {t('checkout.errors.backToGarageList')}
                        </Button>
                    </Box>
                )}
            />
        )
    }

    if (activeReservationQuery.viewStatus === 'error') {
        const errorMessage = activeReservationQuery.error?.message ?? t('checkout.errors.unavailable')

        return (
            <CheckoutMessageState
                message={errorMessage}
                action={garageListAction}
            />
        )
    }

    if (activeReservationQuery.viewStatus === 'unavailable' || !hasAllocatedCheckoutReservation(activeReservationQuery.data)) {
        return (
            <CheckoutMessageState
                message={t('checkout.errors.unavailable')}
                action={garageListAction}
            />
        )
    }

    return (
        <>
            <CheckoutAllocatedView
                viewModel={buildCheckoutViewModel(activeReservationQuery.data)}
                onBack={onBack}
                onPay={onPay}
                isPwa={isPwa}
                checkoutFlow={getCheckoutFlow(location.search)}
            />
            {activeReservationQuery.isPaymentResolutionBlocking ? (
                <CheckoutPaymentResolutionOverlay
                    checkingText={t('checkout.paymentResolution.checking')}
                />
            ) : null}
        </>
    )
}
